import { ScoreInterpretation } from '../types';

/**
 * Returns a simplified interpretation of WISC-V scores.
 * Uses a 5-level scale: Bardzo wysoki, Wysoki, Przeciętny, Niski, Bardzo niski.
 * This is used primarily in SimpleWISCVSystem.tsx.
 */
export const getSimplifiedScoreInterpretation = (
  score: number | string,
  type: 'composite' | 'subtest' = 'composite'
): ScoreInterpretation | '' => {
  if (!score || score === '' || score === 0) return '';

  const numScore = typeof score === 'string' ? parseInt(score, 10) : score;

  if (isNaN(numScore) || numScore <= 0) return '';

  if (type === 'composite') {
    if (numScore >= 130) return 'Bardzo wysoki';
    if (numScore >= 115) return 'Ponadprzeciętny';
    if (numScore >= 85) return 'Przeciętny';
    if (numScore >= 70) return 'Graniczny';
    return 'Bardzo niski';
  } else {
    // Subtest scores (1-19)
    if (numScore >= 16) return 'Bardzo wysoki';
    if (numScore >= 13) return 'Wysoki';
    if (numScore >= 8) return 'Przeciętny';
    if (numScore >= 5) return 'Niski';
    return 'Bardzo niski';
  }
};

// =============================================================================
// WISC-V score range validation
// =============================================================================

/** Valid range for WISC-V subtest scaled scores. */
export const SUBTEST_SCORE_MIN = 1;
export const SUBTEST_SCORE_MAX = 19;

/** Valid range for WISC-V composite/index scores (VCI/VSI/FRI/WMI/PSI/FSIQ). */
export const COMPOSITE_SCORE_MIN = 40;
export const COMPOSITE_SCORE_MAX = 160;

/**
 * Parses a score input (string or number) into an integer, or null when the
 * input is empty, non-numeric or not an integer.
 */
const parseScoreInput = (value: string | number): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : null;
  }
  const trimmed = value.trim();
  // Only plain (optionally negative) integers — rejects "12abc", "10.5", "1e3"
  if (!/^-?\d+$/.test(trimmed)) return null;
  return parseInt(trimmed, 10);
};

/**
 * Returns true only for a valid WISC-V subtest scaled score (integer 1-19).
 * Empty, non-numeric and non-integer inputs are invalid.
 */
export const validateSubtestScore = (value: string | number): boolean => {
  const num = parseScoreInput(value);
  return num !== null && num >= SUBTEST_SCORE_MIN && num <= SUBTEST_SCORE_MAX;
};

/**
 * Returns true only for a valid WISC-V composite/index score (integer 40-160).
 * Empty, non-numeric and non-integer inputs are invalid.
 */
export const validateCompositeScore = (value: string | number): boolean => {
  const num = parseScoreInput(value);
  return num !== null && num >= COMPOSITE_SCORE_MIN && num <= COMPOSITE_SCORE_MAX;
};

/**
 * UI-friendly validation: returns null when the field is empty (not entered
 * yet) or valid, otherwise a Polish error message with the allowed range.
 */
export const getScoreValidationError = (
  value: string | number,
  type: 'composite' | 'subtest'
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const isValid = type === 'subtest' ? validateSubtestScore(value) : validateCompositeScore(value);
  if (isValid) return null;
  return type === 'subtest'
    ? `Dozwolony zakres: ${SUBTEST_SCORE_MIN}-${SUBTEST_SCORE_MAX}`
    : `Dozwolony zakres: ${COMPOSITE_SCORE_MIN}-${COMPOSITE_SCORE_MAX}`;
};

/**
 * Approximates a percentile rank from a WISC-V composite score (M=100, SD=15)
 * using the standard normal cumulative distribution. Returns an integer 1-99
 * (clamped) for use in interpretation prompts.
 */
export const getPercentile = (score: number | string): string => {
  if (score === '' || score === null || score === undefined) return '';
  const num = typeof score === 'string' ? parseInt(score, 10) : score;
  if (!Number.isFinite(num) || num <= 0) return '';
  const z = (num - 100) / 15;
  // Abramowitz & Stegun 26.2.17 approximation of Φ(z)
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp(-(z * z) / 2);
  let p = 1 - d * (((((1.330274429 * t - 1.821255978) * t + 1.781477937) * t - 0.356563782) * t + 0.319381530) * t);
  if (z < 0) p = 1 - p;
  const pct = Math.round(p * 100);
  return String(Math.max(1, Math.min(99, pct)));
};

/**
 * Formats a 95% confidence interval for a WISC-V composite score.
 * SE_M ≈ 5 for index scores; CI 95% ≈ ±10 (1.96 × SE_M, rounded).
 */
export const formatCI = (score: number | string, sem: number = 5): string => {
  if (score === '' || score === null || score === undefined) return '';
  const num = typeof score === 'string' ? parseInt(score, 10) : score;
  if (!Number.isFinite(num) || num <= 0) return '';
  const margin = Math.round(1.96 * sem);
  return `${num - margin}-${num + margin}`;
};
